import axios from 'axios';
import { environment } from '../../../../environment/environment';
import type {
  PendingSummary,
  ProviderPaymentSubmission,
  SavingPaymentBatch,
  SavingPaymentBatchItem,
  SavingsBatchFilters,
  SubmissionTransaction,
  SubmitPendingRequest,
} from '../types';

// ---------------------------------------------------------------------------
// Batches (All batches tab) — unchanged endpoints.
// ---------------------------------------------------------------------------

export const listBatches = async (
  filters: SavingsBatchFilters = {}
): Promise<SavingPaymentBatch[]> => {
  const res = await axios.get(`${environment.baseUrl}/saving_payment_batch/`, {
    params: filters,
  });
  return res.data;
};

export const getBatchItems = async (
  batchId: number
): Promise<SavingPaymentBatchItem[]> => {
  const res = await axios.get(
    `${environment.baseUrl}/saving_payment_batch/${batchId}/items/`
  );
  return res.data;
};

// Per-batch line-item CSV — server-generated (download button (b)). Sync (no
// await): the browser handles the download, matching downloadSubmissionCsv /
// downloadOutstandingCsv below.
export const downloadBatch = (batchId: number): void => {
  window.open(
    `${environment.baseUrl}/saving_payment_batch/${batchId}/download/`
  );
};

// ---------------------------------------------------------------------------
// Provider payment submissions (Payments tab) — Franc-only by server
// enforcement (§5.2); the FE never calls a generic all-providers endpoint.
// ---------------------------------------------------------------------------

const SUBMISSION_BASE = (): string =>
  `${environment.baseUrl}/saving_payment_submission`;

// GET /saving_payment_submission/ — immutable payment history (newest first).
export const listSubmissions = async (): Promise<
  ProviderPaymentSubmission[]
> => {
  const res = await axios.get(`${SUBMISSION_BASE()}/`);
  return res.data;
};

// GET /saving_payment_submission/{id}/transactions/ — per-employee allocations.
export const getSubmissionTransactions = async (
  submissionId: number
): Promise<SubmissionTransaction[]> => {
  const res = await axios.get(
    `${SUBMISSION_BASE()}/${submissionId}/transactions/`
  );
  return res.data;
};

// GET /saving_payment_submission/pending_summary/ — the rolling "ready to pay"
// aggregate rendered as the pinned Outstanding row.
export const getPendingSummary = async (): Promise<PendingSummary> => {
  const res = await axios.get(`${SUBMISSION_BASE()}/pending_summary/`);
  return res.data;
};

// GET /saving_payment_submission/pending_transactions/ — lines in the pool.
export const getPendingTransactions = async (): Promise<
  SubmissionTransaction[]
> => {
  const res = await axios.get(`${SUBMISSION_BASE()}/pending_transactions/`);
  return res.data;
};

// POST /saving_payment_submission/submit_pending/ — crystallise the outstanding
// pool into a real submission. Provider is pinned FRANC server-side (no field).
export const submitPending = async (
  body: SubmitPendingRequest = {}
): Promise<ProviderPaymentSubmission> => {
  const res = await axios.post(`${SUBMISSION_BASE()}/submit_pending/`, body);
  return res.data;
};

// CSV of one payment — server-generated (download button (a)).
export const downloadSubmissionCsv = (submissionId: number): void => {
  window.open(`${SUBMISSION_BASE()}/${submissionId}/download/`);
};

// CSV of the current outstanding pool — server-generated (download button (c)).
export const downloadOutstandingCsv = (): void => {
  window.open(`${SUBMISSION_BASE()}/pending_download/`);
};
